/**
 * Platform detection utilities
 * Detects user's device platform (iOS, Android, Desktop) and provides appropriate app store links
 */

export type Platform = 'ios' | 'android' | 'desktop';

export interface AppStoreLinks {
  ios: string;
  android: string;
}

/**
 * Detect the user's platform based on user agent
 */
export function detectPlatform(): Platform {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'desktop';
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // Check for iOS
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'ios';
  }

  // Check for Android
  if (/android/i.test(userAgent)) {
    return 'android';
  }

  return 'desktop';
}

/**
 * Get the appropriate app store link based on the user's platform
 */
export function getAppStoreLink(links: AppStoreLinks): string {
  const platform = detectPlatform();
  
  switch (platform) {
    case 'ios':
      return links.ios;
    case 'android':
      return links.android;
    default:
      // Default to iOS for desktop users
      return links.ios;
  }
}

/**
 * Get the translation key for platform-specific call-to-action text
 * Use with i18n t() function: t(getPlatformCTAKey())
 */
export function getPlatformCTAKey(): string {
  const platform = detectPlatform();
  
  const keys = {
    ios: 'mobileApp.downloadAppStore',
    android: 'mobileApp.downloadGooglePlay',
    desktop: 'mobileApp.downloadMobileApp'
  };
  
  return keys[platform];
}

/**
 * Check if user is on a mobile device
 */
export function isMobileDevice(): boolean {
  const platform = detectPlatform();
  return platform === 'ios' || platform === 'android';
}

/**
 * Get the platform name for display
 */
export function getPlatformName(): string {
  const platform = detectPlatform();
  
  const names = {
    ios: 'iOS',
    android: 'Android',
    desktop: 'Desktop'
  };
  
  return names[platform];
}

